/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `SectorCascadeFields` for the Caintra Connect frontend.
 * @module components/AppointmentRegistrationSections/CorporateIdentity/SectorCascadeFields
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import Select from "react-select";
import {
  getSafeSelectedOption,
  getSelectStylesWithError,
} from "../../../utils/common";

const FIELD_NOT_EDITABLE_KEY = "components.corporateIdentity.fieldNotEditable";

const referencedDataSelectProps = {
  getOptionLabel: (option) => option.nombre,
  getOptionValue: (option) => String(option._id),
};

const ReadOnlyReferenceSelect = ({
  fieldKey,
  label,
  placeholder,
  value,
  options,
  onChange,
  getFieldValidation,
  missingRequiredFields,
  inputGroupClassName,
  t,
  styles,
}) => (
  <div className={inputGroupClassName || styles.inputGroup}>
    <label className={styles.inputLabel}>
      {label}
      {missingRequiredFields &&
        Object.hasOwn(missingRequiredFields, fieldKey) && (
          <span className={styles.required}> *</span>
        )}
    </label>
    <Select
      value={getSafeSelectedOption(options, value)}
      placeholder={placeholder}
      styles={getSelectStylesWithError(
        getFieldValidation && getFieldValidation(fieldKey),
      )}
      className="select__control"
      isDisabled={true}
      options={options}
      {...referencedDataSelectProps}
      onChange={onChange}
    />
    <p className={styles.notEditableFieldMessage}>{t(FIELD_NOT_EDITABLE_KEY)}</p>
  </div>
);

const SectorCascadeFields = ({
  company,
  sectors,
  subSectors,
  branches,
  businessGroups,
  sectorEconomico,
  subSectorSCIAN,
  ramaSCIAN,
  grupoEmpresarial,
  onSectorEconomicoChange,
  onSubSectorChange,
  onRamaChange,
  onGrupoEmpresarialChange,
  getFieldValidation,
  missingRequiredFields,
  isMobile,
  t,
  styles,
}) => (
  <>
    {company && Object.hasOwn(company, "sectorEconomico") && (
      <ReadOnlyReferenceSelect
        fieldKey="sectorEconomico"
        label={t("components.corporateIdentity.economicSectorLabel")}
        placeholder={t("components.corporateIdentity.economicSectorPlaceholder")}
        value={sectorEconomico}
        options={sectors}
        onChange={onSectorEconomicoChange}
        getFieldValidation={getFieldValidation}
        missingRequiredFields={missingRequiredFields}
        t={t}
        styles={styles}
      />
    )}
    {company && Object.hasOwn(company, "ramaSCIAN") && (
      <ReadOnlyReferenceSelect
        fieldKey="ramaSCIAN"
        label={t("components.corporateIdentity.economicActivityLabel")}
        placeholder={t(
          "components.corporateIdentity.economicActivityPlaceholder",
        )}
        value={ramaSCIAN}
        options={branches}
        onChange={onRamaChange}
        getFieldValidation={getFieldValidation}
        missingRequiredFields={missingRequiredFields}
        inputGroupClassName={`${styles.inputGroup} ${
          isMobile ? styles.mobileTopSpacing : ""
        }`}
        t={t}
        styles={styles}
      />
    )}
    {company && Object.hasOwn(company, "subSectorSCIAN") && (
      <ReadOnlyReferenceSelect
        fieldKey="subSectorSCIAN"
        label={t("components.corporateIdentity.subsectorLabel")}
        placeholder={t("components.corporateIdentity.subsectorPlaceholder")}
        value={subSectorSCIAN}
        options={subSectors}
        onChange={onSubSectorChange}
        getFieldValidation={getFieldValidation}
        missingRequiredFields={missingRequiredFields}
        t={t}
        styles={styles}
      />
    )}
    {company && Object.hasOwn(company, "grupoEmpresarial") && (
      <ReadOnlyReferenceSelect
        fieldKey="grupoEmpresarial"
        label={t("components.corporateIdentity.businessGroupLabel")}
        placeholder={t("components.corporateIdentity.businessGroupPlaceholder")}
        value={grupoEmpresarial}
        options={businessGroups}
        onChange={onGrupoEmpresarialChange}
        getFieldValidation={getFieldValidation}
        missingRequiredFields={missingRequiredFields}
        t={t}
        styles={styles}
      />
    )}
  </>
);

export default SectorCascadeFields;
